/**
 * Decision simulator: describe a situation, see where it lands in the queue
 * and why it sits above or below everything else.
 *
 * The bands, cohorts and priority numbers here mirror buildStandardQueue and
 * compareByTier in src/domain/queueEngine.ts. Keeping the arithmetic visible
 * is the point: the queue looks like a magic list until you can see that a
 * missed call is worth 600 minus its age in minutes.
 */
import { useState } from 'react';
import clsx from 'clsx';
import { Choice, Playground, Segmented, Slider, Stat } from './primitives';

type ScenarioKey =
  | 'depositOnFile'
  | 'depositLive'
  | 'depositExpired'
  | 'resendDateTaken'
  | 'unreadMessage'
  | 'missedCall'
  | 'aiHandoff'
  | 'pushedLead'
  | 'rebookFollowup'
  | 'healingCheck';

type Cohort = 'fresh' | 'money' | 'open business';

interface Verdict {
  visible: boolean;
  hiddenBecause?: string;
  cohort: Cohort;
  band: 'Now' | 'Today';
  priority: number;
  workingLabel: string;
  why: string;
  leavesWhen: string;
}

interface Scenario {
  label: string;
  /** What the age slider measures for this situation. */
  ageLabel: string;
  ageMax: number;
  ageDefault: number;
  evaluate: (ageHours: number) => Verdict;
}

const TIER_OF: Record<Cohort, number> = { fresh: 1, money: 2, 'open business': 3 };

const SCENARIOS: Record<ScenarioKey, Scenario> = {
  depositOnFile: {
    label: 'A deposit is paid but no date is locked in',
    ageLabel: 'Days since the deposit landed',
    ageMax: 24 * 60,
    ageDefault: 24 * 9,
    evaluate: () => ({
      visible: true,
      cohort: 'money',
      band: 'Today',
      priority: 500,
      workingLabel: 'Flat 500, it does not decay',
      why: 'Money is in the door with nothing to show for it. It sits in the money lane until someone gives it a date.',
      leavesWhen: 'A booking is placed against the deposit.',
    }),
  },
  depositLive: {
    label: 'A deposit link is out and still live',
    ageLabel: 'Hours until the link expires',
    ageMax: 60,
    ageDefault: 20,
    evaluate: (hoursLeft) => {
      if (hoursLeft > 48) {
        return {
          visible: false,
          hiddenBecause:
            'The queue only picks a live link up inside the last 48 hours. Before that the client simply has not had time yet.',
          cohort: 'money',
          band: 'Today',
          priority: 0,
          workingLabel: '-',
          why: '',
          leavesWhen: '',
        };
      }
      const soon = hoursLeft <= 4;
      return {
        visible: true,
        cohort: 'money',
        band: soon ? 'Now' : 'Today',
        priority: soon ? 900 : 500,
        workingLabel: soon ? '900, the expiring-soon rate' : '500, the standard money rate',
        why: soon
          ? 'Under four hours left. If nobody chases it, the hold drops and the slot goes back on the calendar.'
          : 'The link is live and the clock is running, but there is still room to let the client get to it.',
        leavesWhen: 'The client pays, or the link expires and it becomes a resend job.',
      };
    },
  },
  depositExpired: {
    label: 'A deposit link expired without being paid',
    ageLabel: 'Days since it expired',
    ageMax: 24 * 14,
    ageDefault: 24 * 2,
    evaluate: (hours) => {
      const days = hours / 24;
      if (days > 7) {
        return {
          visible: false,
          hiddenBecause:
            'After seven days an expired link stops being a live job. It is stale, and chasing it reads as a cold lead rather than a resend.',
          cohort: 'money',
          band: 'Today',
          priority: 0,
          workingLabel: '-',
          why: '',
          leavesWhen: '',
        };
      }
      return {
        visible: true,
        cohort: 'money',
        band: 'Today',
        priority: 500,
        workingLabel: 'Flat 500, with an expired flag on the card',
        why: 'The client wanted the slot enough to be sent a link. That intent is worth a week of chasing.',
        leavesWhen: 'The link is resent, which supersedes this card, or seven days pass.',
      };
    },
  },
  resendDateTaken: {
    label: 'You went to resend an expired link and the date had gone',
    ageLabel: 'Hours since you tried',
    ageMax: 72,
    ageDefault: 2,
    evaluate: () => ({
      visible: true,
      cohort: 'money',
      band: 'Today',
      priority: 990,
      workingLabel: '990, the highest priority in the queue bar a firing callback',
      why: 'Someone paid attention, wanted a date, and now cannot have it. No link is sent automatically, because the conversation has to happen first.',
      leavesWhen: 'A new date is agreed and a fresh link goes out.',
    }),
  },
  unreadMessage: {
    label: 'A client messaged and nobody has replied',
    ageLabel: 'Hours since they wrote',
    ageMax: 24,
    ageDefault: 2,
    evaluate: (hours) => {
      if (hours > 6) {
        return {
          visible: false,
          hiddenBecause:
            'Past six hours an unanswered message stops being a queue item. It stays in Conversations, where the messaging team owns it.',
          cohort: 'fresh',
          band: 'Today',
          priority: 0,
          workingLabel: '-',
          why: '',
          leavesWhen: '',
        };
      }
      return {
        visible: true,
        cohort: 'fresh',
        band: 'Today',
        priority: Math.round(550 - hours * 60),
        workingLabel: '550 minus one point per minute waited',
        why: 'Fresh contact is answered oldest first, so nobody gets skipped just because someone newer looks more interesting.',
        leavesWhen: 'Anyone sends a reply.',
      };
    },
  },
  missedCall: {
    label: 'A client rang and nobody picked up',
    ageLabel: 'Hours since the call',
    ageMax: 96,
    ageDefault: 6,
    evaluate: (hours) => {
      if (hours > 72) {
        return {
          visible: false,
          hiddenBecause: 'Three days is the limit for treating a missed call as live.',
          cohort: 'fresh',
          band: 'Today',
          priority: 0,
          workingLabel: '-',
          why: '',
          leavesWhen: '',
        };
      }
      return {
        visible: true,
        cohort: 'fresh',
        band: 'Today',
        priority: Math.round(600 - hours * 60),
        workingLabel: '600 minus one point per minute waited',
        why: 'The window is three days rather than one, so a Friday evening voicemail is still sitting there on Monday morning.',
        leavesWhen: 'Someone calls them back.',
      };
    },
  },
  aiHandoff: {
    label: 'The AI assistant asked for a human',
    ageLabel: 'Minutes since the handoff',
    ageMax: 12,
    ageDefault: 1,
    evaluate: () => ({
      visible: true,
      cohort: 'fresh',
      band: 'Now',
      priority: 950,
      workingLabel: '950, near the top of the board',
      why: 'A client is mid-conversation and has hit something the assistant will not answer. They are waiting right now.',
      leavesWhen: 'An agent picks the conversation up.',
    }),
  },
  pushedLead: {
    label: 'An agent pushed a lead over from Conversations',
    ageLabel: 'Hours since the push',
    ageMax: 72,
    ageDefault: 3,
    evaluate: () => ({
      visible: true,
      cohort: 'fresh',
      band: 'Today',
      priority: 100,
      workingLabel: '100, low by design',
      why: 'It is real work, but nothing about it is urgent yet. It sits in fresh so it is worked oldest first rather than jumping a queue.',
      leavesWhen: 'A deposit is paid or a booking is placed.',
    }),
  },
  rebookFollowup: {
    label: 'A client left saying they would rebook later',
    ageLabel: 'Hours since they left',
    ageMax: 24 * 10,
    ageDefault: 24 * 2,
    evaluate: (hours) => {
      const stale = hours > 24 * 5;
      const base = Math.max(380, 520 - hours * 2);
      return {
        visible: true,
        cohort: 'open business',
        band: 'Today',
        priority: Math.round(stale ? base - 30 : base),
        workingLabel: stale
          ? '520 minus two points an hour, floored at 380, then 30 off for going stale'
          : '520 minus two points an hour, floored at 380',
        why: stale
          ? 'Past five days the intent has cooled. It stays in the queue but stops competing with fresher open business.'
          : 'They meant it when they said it. The value of the follow-up decays slowly rather than falling off a cliff.',
        leavesWhen: 'The follow-up is marked booked, declined or no contact.',
      };
    },
  },
  healingCheck: {
    label: 'A session finished and the healing check is due',
    ageLabel: 'Days since the session',
    ageMax: 24 * 21,
    ageDefault: 24 * 8,
    evaluate: (hours) => {
      if (hours < 24 * 7) {
        return {
          visible: false,
          hiddenBecause:
            'The healing check is due seven days after the session. Before then there is nothing useful to ask.',
          cohort: 'open business',
          band: 'Today',
          priority: 0,
          workingLabel: '-',
          why: '',
          leavesWhen: '',
        };
      }
      return {
        visible: true,
        cohort: 'open business',
        band: 'Today',
        priority: 360,
        workingLabel: 'Flat 360',
        why: 'A week out is when a client can tell you how it settled, and it is a natural opening to talk about the next sitting.',
        leavesWhen: 'The check is resolved.',
      };
    },
  },
};

const SCENARIO_OPTIONS = (Object.keys(SCENARIOS) as ScenarioKey[]).map((k) => ({
  value: k,
  label: SCENARIOS[k].label,
}));

const COHORT_TONE: Record<Cohort, string> = {
  fresh: 'text-azure border-azure/40 bg-azure/10',
  money: 'text-gold-bright border-gold-dim/50 bg-gold/10',
  'open business': 'text-violet border-violet/40 bg-violet/10',
};

// Which role picks each lane up (bd tv-570). The one shared list was split into
// two screens: the booking agent works fresh contact on My Queue; the Operations
// Lead works the money + open-business tail on the Sweeper.
const ROLE_OF: Record<Cohort, { who: string; blurb: string }> = {
  fresh: {
    who: 'Booking agent',
    blurb: "Fresh contact is the booking agent's job — it shows on their My Queue.",
  },
  money: {
    who: 'Sweeper (Ops lead)',
    blurb:
      "Deposits are the Sweeper's job — they show on the Operations Lead's Money & open business list, not the agent's queue.",
  },
  'open business': {
    who: 'Sweeper (Ops lead)',
    blurb:
      "Open business is the Sweeper's job — it shows on the Operations Lead's Money & open business list, not the agent's queue.",
  },
};

export default function QueueLaneSimulator() {
  const [key, setKey] = useState<ScenarioKey>('depositLive');
  const [ageHours, setAgeHours] = useState(SCENARIOS.depositLive.ageDefault);
  const [attempts, setAttempts] = useState<'1' | '2' | '3'>('1');

  const scenario = SCENARIOS[key];
  const verdict = scenario.evaluate(ageHours);

  const pickScenario = (next: ScenarioKey) => {
    setKey(next);
    setAgeHours(SCENARIOS[next].ageDefault);
  };

  const formatAge = (v: number): string => {
    if (scenario.ageMax <= 12) return `${v} min`;
    if (scenario.ageMax <= 96) return `${v} hr`;
    const d = Math.round((v / 24) * 10) / 10;
    return `${d} ${d === 1 ? 'day' : 'days'}`;
  };

  // The money lane is the one place the three-strikes rule does not apply.
  const isMoney = verdict.cohort === 'money';
  const goesRed = !isMoney;

  return (
    <Playground
      title="Where would this land?"
      instruction="Pick something that has just happened in the studio and see which lane it falls into, who works it — the booking agent or the Sweeper — how high it sits, and what makes it go away."
      footnote="Priority is only a sort key, never a score anyone is judged on. Items are grouped into lanes first and only ranked inside their own lane. Fresh contact goes to the booking agent's My Queue; deposits and open business go to the Operations Lead's Sweeper."
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
        <div className="space-y-5">
          <Choice label="What has happened" value={key} options={SCENARIO_OPTIONS} onChange={pickScenario} />
          <Slider
            label={scenario.ageLabel}
            value={ageHours}
            min={0}
            max={scenario.ageMax}
            step={scenario.ageMax <= 96 ? 1 : 12}
            onChange={setAgeHours}
            format={formatAge}
          />
          <Segmented
            label="Times you have tried to reach them"
            value={attempts}
            options={[
              { value: '1', label: 'Once' },
              { value: '2', label: 'Twice' },
              { value: '3', label: 'Three times' },
            ]}
            onChange={setAttempts}
          />
        </div>

        <div className="space-y-3.5">
          {verdict.visible ? (
            <>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                <div
                  className={clsx(
                    'rounded-md border px-3.5 py-3',
                    COHORT_TONE[verdict.cohort],
                  )}
                >
                  <p className="label-small-caps opacity-80">Lane</p>
                  <p className="font-inter text-base font-semibold capitalize mt-1">{verdict.cohort}</p>
                  <p className="font-inter text-[11px] opacity-75 mt-0.5">
                    {ROLE_OF[verdict.cohort].who} · worked{' '}
                    {TIER_OF[verdict.cohort] === 1 ? 'first' : TIER_OF[verdict.cohort] === 2 ? 'second' : 'third'}
                  </p>
                </div>
                <Stat
                  label="Section"
                  value={verdict.band}
                  caption={verdict.band === 'Now' ? 'Top of the screen.' : "Today's list."}
                />
                <Stat
                  label="Priority"
                  value={String(verdict.priority)}
                  strong
                  caption={verdict.workingLabel}
                />
              </div>

              <div className="rounded-md border border-gold-dim/20 bg-ink-black px-4 py-3.5">
                <p className="label-small-caps text-gold-bright">Why it sits there</p>
                <p className="font-serif text-cream-soft mt-1.5 leading-relaxed">{verdict.why}</p>
              </div>

              <div className="rounded-md border border-gold-dim/20 bg-ink-black px-4 py-3.5">
                <p className="label-small-caps text-cream-faint">What makes it disappear</p>
                <p className="font-serif text-cream-soft mt-1.5 leading-relaxed">{verdict.leavesWhen}</p>
              </div>

              <div className="rounded-md border border-gold-dim/20 bg-ink-black px-4 py-3.5">
                <p className="label-small-caps text-gold-bright">Who works it</p>
                <p className="font-serif text-cream-soft mt-1.5 leading-relaxed">
                  {ROLE_OF[verdict.cohort].blurb}
                </p>
              </div>

              {verdict.cohort === 'fresh' ? (
                <div className="rounded-md border border-azure/40 bg-azure/10 px-4 py-3.5">
                  <p className="label-small-caps text-azure">Worked oldest first</p>
                  <p className="font-serif text-cream-soft mt-1.5 leading-relaxed">
                    Fresh contact is the one lane that ignores priority and goes in strict order of arrival, so a quiet
                    enquiry from this morning is answered before a livelier one from this afternoon.
                  </p>
                </div>
              ) : null}

              <div
                className={clsx(
                  'rounded-md border px-4 py-3.5',
                  goesRed ? 'border-gold-dim/25 bg-ink-black' : 'border-mint/40 bg-mint/10',
                )}
              >
                <p className={clsx('label-small-caps', goesRed ? 'text-cream-faint' : 'text-mint')}>
                  After {attempts === '1' ? 'one attempt' : attempts === '2' ? 'two attempts' : 'three attempts'}
                </p>
                <p className="font-serif text-cream-soft mt-1.5 leading-relaxed">
                  {attempts === '3' ? (
                    goesRed ? (
                      <>
                        Three tries with no answer and this drops to the red list, where automated outreach picks it up
                        instead of a person. If they ever reply it comes straight back to the top as a fresh enquiry.
                      </>
                    ) : (
                      <>
                        This is money in the door, so it never goes red. It re-arms at the third attempt and stays on
                        the board indefinitely. Nobody quietly gives up on a client who has already paid you.
                      </>
                    )
                  ) : (
                    <>
                      Each attempt arms a 24 hour timer. While that timer runs the card drops to the bottom of the
                      board so you are not ringing the same person twice in an afternoon. When it elapses, the card
                      moves to attempt {Number(attempts) + 1}.
                    </>
                  )}
                </p>
              </div>
            </>
          ) : (
            <div className="rounded-lg border border-gold-dim/25 bg-ink-black px-4 py-5">
              <p className="label-small-caps text-cream-faint">It does not appear in the queue</p>
              <p className="font-serif text-cream-soft mt-2 leading-relaxed">{verdict.hiddenBecause}</p>
            </div>
          )}
        </div>
      </div>
    </Playground>
  );
}
